import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { storeToRefs } from 'pinia'
import { deviceCalibration } from '@rapoo/device-sdk'
import { useKeyboardReport } from '../useKeyboardReport'
import { defaultReportError } from '@/composables/useErrorLogger'

/**
 * 校准模式
 * 调用后自动进入校准模式与上报数据监听
 * 提供calibration，saveCalibration以供正式开始校准
 */
const calibratedKeys = ref<{ index: number }[]>([])

export function useCalibrationMode() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const keyTravel = computed(() => {
    return currentDevice.value?.features.keyTravel
  })

  /* ------------ 校准模式 ------------ */
  // 是否进入校准模式
  const switchCalibration = ref(false)
  const switchCalibrationMode = async (type = false) => {
    if (!keyTravel.value) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    try {
      await keyTravel.value.switchCalibrationMode(type)
      switchCalibration.value = type
    } catch (error) {
      console.log('switchCalibrationMode error', error)
      defaultReportError(error)
    }
  }
  onMounted(() => {
    switchCalibrationMode(true)
  })
  onUnmounted(() => {
    switchCalibrationMode(false)
  })
  /* ------------ 校准模式 ------------ */

  /* ------------ 开始校准 ------------ */
  // 是否开启自适应校准
  const switchAdaptive = ref(false)
  const switchAdaptiveCalibration = async () => {
    if (!keyTravel.value) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    try {
      await keyTravel.value.switchAdaptiveCalibration(!switchAdaptive.value)
    } catch (error) {
      console.log('switchAdaptiveCalibration error', error)
      defaultReportError(error)
    }
  }
  // 是否正在校准
  const startCalibration = ref(false)
  // 校准
  const calibration = async () => {
    if (!keyTravel.value) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    try {
      await keyTravel.value.switchCalibrationMode(true)
      await keyTravel.value.calibration('SMD')
      startCalibration.value = true
    } catch (error) {
      console.log('calibration error', error)
      defaultReportError(error)
    }
  }
  // 保存校准
  const saveCalibration = async () => {
    if (!keyTravel.value) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    try {
      await keyTravel.value.saveCalibration('SMD')
      startCalibration.value = false
      calibratedKeys.value = []
    } catch (error) {
      console.log('saveCalibration error', error)
      defaultReportError(error)
    }
  }
  /* ------------ 开始校准 ------------ */

  /* ------------ 硬件数据上报 ------------ */
  const dispatchCalibrationModeData = (calibData: deviceCalibration) => {
    // 仅在校准进行中时收集按键
    if (!startCalibration.value) return

    const { index, NormalisedDelta, MaxDeltaStatus, SampleMaxDelta } = calibData
    if (NormalisedDelta > 254 && MaxDeltaStatus === 2 && SampleMaxDelta > 6000) {
      const exists = calibratedKeys.value.some((k) => k.index === index)
      if (!exists) {
        calibratedKeys.value = [...calibratedKeys.value, { index }]
      }
    }
  }
  /* ------------ 硬件数据上报 ------------ */
  const { setup, teardown } = useKeyboardReport(dispatchCalibrationModeData)

  onMounted(() => {
    setup(dispatchCalibrationModeData)
  })

  onUnmounted(() => {
    teardown()
  })

  return {
    switchAdaptive,
    startCalibration,
    calibratedKeys,
    switchAdaptiveCalibration,
    calibration,
    saveCalibration,
  }
}
